import JWT from 'jsonwebtoken';
import Vue from 'vue';
import Vuex from 'vuex';
import dateformat from 'dateformat';

import { Token, User, ModalComponents, ModalState, ModalData, Room, Appointment, Company, AlertState, Alert, DayOverview } from '@/definitions';

import { loadPersistedState, persistedStatePlugin } from './plugins/persisted-state';
import {
  RootState,
  LocalContext,
  LocalUser,
  Actions,
  Action,
  Mutations,
  Mutation,
  Getter,
  Getters
} from './store.definitions';

type Context = LocalContext<RootState>;
Vue.use(Vuex);

const initialState: RootState = {
  token: {
    originalValue: '',
    exp: 0,
    iat: 0,
  },
  user: {
    _id: '',
    email: '',
    firstName: '',
    lastName: '',
    isAdmin: false,
    company: '',
    companyId: '',
    color: 'purple',
    language: 'nl',
    hasStats: false,
    isAppAdmin: false,
    subscriptionEnd: ''
  },
  modal: {
    active: false,
    component: ModalComponents.APPOINTMENT,
    props: {}
  },
  date: { date: new Date() },
  appointments: [],
  myAppointments: [],
  weekOverview: [],
  company: {
    company: {
      _id: '',
      name: '',
      permission: 0,
      permissions: [
        { name: "ADD_MANAGERS", value: 1, isEnabled: true },
        { name: "MULTIPLE_RESERVATIONS", value: 2, isEnabled: true },
        { name: "CREATE_APPOINTMENT_FOR_USER", value: 4, isEnabled: true }
      ],
      calendar: {
        workingDays: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false
        },
        additionalDays: []
      }
    },
    rooms: [],
  },
  alert: {
    active: false,
    text: '',
    type: ''
  }
};

const visibleHour = (hour: number) => {
  const h = (hour < 10) ? `0${hour}` : hour;
  return `${h}:00`;
}

const mutations: Mutations<RootState> = {
  [Mutation.STORE_TOKEN](state: RootState, newToken: Token) {
    state.token = { ...newToken };
  },

  [Mutation.STORE_USER](state: RootState, newUser: LocalUser) {
    state.user = { ...newUser };
  },

  [Mutation.STORE_COMPANY](state: RootState, company: { company: Company; rooms: Room[] }) {
    state.company = company;
  },

  [Mutation.SET_MODAL_STATE](state: RootState, newModalState: ModalState) {
    state.modal = { ...newModalState };
  },

  [Mutation.SET_ALERT_STATE](state: RootState, newAlertState: AlertState) {
    state.alert = { ...newAlertState };
  },

  [Mutation.SET_CURRENT_DATE](state: RootState, newCurrentDate: { date: Date }) {
    state.date = newCurrentDate;
  },

  [Mutation.SET_APPOINTMENTS](state: RootState, newAppointments: Room[]) {
    state.appointments = newAppointments;
  },

  [Mutation.SET_MY_APPOINTMENTS](state: RootState, newAppointments: Appointment[]) {
    state.myAppointments = newAppointments;
  },

  [Mutation.SET_WEEK_OVERVIEW](state: RootState, weekOverview: DayOverview[]) {
    state.weekOverview = weekOverview;
  },
};

const actions: Actions<RootState> = {
  [Action.UPDATE_USERTOKEN]({ commit }: Context, rawToken: string) {
    const token = JWT.decode(rawToken) as User & Token;
    const newUser: LocalUser = {
      _id: token._id,
      email: token.email,
      firstName: token.firstName,
      lastName: token.lastName,
      isAdmin: token.isAdmin,
      company: token.company,
      companyId: token.companyId,
      color: token.color,
      language: token.language,
      hasStats: token.hasStats,
      isAppAdmin: token.isAppAdmin,
      subscriptionEnd: token.subscriptionEnd
    };

    const newToken: Token = {
      exp: token.exp,
      iat: token.iat,
      originalValue: rawToken,
    };

    commit(Mutation.STORE_USER, newUser);
    commit(Mutation.STORE_TOKEN, newToken);
  },

  [Action.UPDATE_USER]({ commit }: Context, user: User) {
    const newUser: LocalUser = {
      _id: this.state.user._id,
      email: this.state.user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      isAdmin: this.state.user.isAdmin,
      company: this.state.user.company,
      companyId: this.state.user.companyId,
      color: user.color,
      language: user.language,
      hasStats: user.hasStats,
      isAppAdmin: user.isAppAdmin,
      subscriptionEnd: user.subscriptionEnd
    };

    commit(Mutation.STORE_USER, newUser);
  },

  [Action.LOGOUT_USER]({ commit }: Context) {
    commit(Mutation.STORE_USER, {});
    commit(Mutation.STORE_TOKEN, '');
    commit(Mutation.SET_CURRENT_DATE, { date: new Date() });
  },

  [Action.SHOW_MODAL]({ commit }: Context, componentData: ModalData) {
    const newModalState: ModalState = {
      ...componentData,
      active: true,
    };
    commit(Mutation.SET_MODAL_STATE, newModalState);
  },

  [Action.HIDE_MODAL]({ commit, state }: Context) {
    const newModalState: ModalState = {
      ...state.modal,
      active: false,
    };
    commit(Mutation.SET_MODAL_STATE, newModalState);
  },

  [Action.SHOW_ALERT]({ commit }: Context, alertData: Alert) {
    const newAlertState: AlertState = {
      ...alertData,
      active: true,
    };
    commit(Mutation.SET_ALERT_STATE, newAlertState);

    setTimeout(() => {
      newAlertState.active = false;
      commit(Mutation.SET_ALERT_STATE, newAlertState);
    }, 5000);
  },

  [Action.HIDE_ALERT]({ commit, state }: Context) {
    const newAlertState: AlertState = {
      ...state.alert,
      active: false,
    };
    commit(Mutation.SET_ALERT_STATE, newAlertState);
  },

  [Action.SET_NEW_DATE]({ commit }: Context, date: Date) {
    commit(Mutation.SET_CURRENT_DATE, { date });
  },

  [Action.SAVE_APPOINTMENTS]({ commit }: Context, rooms: Room[]) {
    commit(Mutation.SET_APPOINTMENTS, rooms);
  },

  [Action.SAVE_WEEK_OVERVIEW]({ commit }: Context, overview: DayOverview[]) {
    commit(Mutation.SET_WEEK_OVERVIEW, overview);
  },

  [Action.SAVE_MY_APPOINTMENTS]({ commit }: Context, appointments: Appointment[]) {
    appointments.sort((a, b) =>
      (a.date > b.date) ? 1 : ((b.date > a.date) ? -1 :
        (a.startTime > b.startTime) ? 1 : ((b.startTime > a.startTime) ? -1 : 0)));

    appointments.map(appointment => {
      const d = appointment.date + '';
      const date = new Date(+d.substr(0, 4), +d.substr(4, 2) - 1, +d.substr(6, 2));
      appointment.date = dateformat(date, 'dd-mm-yyyy');
      appointment.startTime = visibleHour(appointment.startTime as number);
      appointment.endTime = visibleHour(appointment.endTime as number);
    });
    commit(Mutation.SET_MY_APPOINTMENTS, appointments);
  },

  [Action.UPDATE_COMPANY]({ commit, state }: Context, company: { company: Company; rooms: Room[] }) {
    const user: LocalUser = {
      ...state.user,
      company: company.company.name
    };
    commit(Mutation.STORE_USER, user);
    commit(Mutation.STORE_COMPANY, company);
  },
};

const getters: Getters<RootState> = {
  [Getter.TOKEN](state: RootState) {
    return state.token.originalValue || '';
  },

  [Getter.USERNAME](state: RootState) {
    return state.user.firstName;
  },

  [Getter.MODAL_ACTIVE](state: RootState) {
    return state.modal.active;
  }
};

const store = new Vuex.Store({
  state: loadPersistedState(initialState),
  mutations,
  actions,
  getters,
  plugins: [persistedStatePlugin],
});

export { Action, Getter } from './store.definitions';
export default store;